import {
    DEFAULT_CRITERIA_PARAMETER_NAME,
    parseSearchCriteria
} from '@corratech/pylot-filters-and-sort/src/hooks/use-plp-manager'
import type { Option } from '@pylot-data/hooks/product/use-product-ui'
import type {
    ConfigurableProductOptions,
    ConfigurableVariant
} from '@pylot-data/pylotschema'
import { useRouter } from 'next/router'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useMemo, useState } from 'react'
import type { SelectedOptions } from '../../types/SelectedOptions'
import { convertPreselectedOptions } from './utils/convertPreselectedOptions'
import { getAvailableOptionValues } from './utils/getAvailableOptionValues'
import { getDefaultSelectedOptions } from './utils/getDefaultSelectedOptions'
import { getProductOptions } from './utils/getProductOptions'

export type UseOptionSelectorReturn = {
    selectedOptions: SelectedOptions
    setSelectedOptions: Dispatch<SetStateAction<SelectedOptions>>
    options: Option[]
}

/**
 * Hook that is responsible for product option API.
 * Should be used in combination with `<VariantSelector />`.
 * @param configurableOptions product configurable options
 * @param preselectedOptions preselected options, e.g. on CartItem.
 * Example: { 93: "Black White", 187: "S" }
 * @param showFirstProductAsDefault
 * @param variants variantes del producto. Desde EVO-245 `configurable_options` trae
 * tambien las opciones sin stock, asi que se usan para que la seleccion por defecto
 * caiga siempre sobre una comprable.
 * @returns product options API
 */
export const useOptionSelector = (
    configurableOptions: ConfigurableProductOptions[] | undefined,
    preselectedOptions?: Record<string, string>,
    showFirstProductAsDefault?: boolean,
    variants?: ConfigurableVariant[] | null
): UseOptionSelectorReturn => {
    const { query, asPath } = useRouter()
    const filterParams = useMemo(
        () =>
            parseSearchCriteria(
                query[DEFAULT_CRITERIA_PARAMETER_NAME] as string
            ),
        [query]
    )

    const availableValues = useMemo(
        () => getAvailableOptionValues(variants),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(variants)]
    )

    const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>(
        getDefaultSelectedOptions(
            configurableOptions,
            filterParams,
            undefined,
            availableValues
        )
    )

    const options = useMemo(
        () => getProductOptions(configurableOptions),
        [configurableOptions]
    )

    const getOptionValues = (attributeCode: string) => {
        return (
            (configurableOptions &&
                configurableOptions.find(
                    (option) => option.attribute_code == attributeCode
                )?.values) ||
            []
        )
    }

    const getAttributeName = (id: string, code: string) => {
        if (configurableOptions) {
            const optionValues = getOptionValues(code)

            if (optionValues) {
                return optionValues.find(
                    (value) => value?.value_index?.toString() === id
                )?.store_label
            }
        }
    }

    // Update selected options to have initial values
    // when configurable options are not set and are loaded
    useEffect(() => {
        if (selectedOptions && !Object.keys(selectedOptions).length) {
            const defaultOptions = getDefaultSelectedOptions(
                configurableOptions,
                filterParams,
                undefined,
                availableValues
            )
            setSelectedOptions(() => defaultOptions)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configurableOptions?.length, setSelectedOptions, query])

    // Set selected options from `preselectedOptions` prop.
    // Reset options on url change (PDP -> PDP transition)
    useEffect(() => {
        const convertedPreselectedOptions = convertPreselectedOptions(
            configurableOptions,
            preselectedOptions
        )

        const defaultOptions = getDefaultSelectedOptions(
            configurableOptions,
            filterParams,
            showFirstProductAsDefault,
            availableValues
        )
        const optionsFromPreselected = {
            ...defaultOptions,
            ...convertedPreselectedOptions
        }

        setSelectedOptions(optionsFromPreselected)

        // Is it important to stringify, otherwise after the first selection selected
        // options jump back to the preselected ones due to the extra useEffect cycle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        asPath,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        JSON.stringify(configurableOptions),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        JSON.stringify(preselectedOptions)
        //setSelectedOptions
    ])

    useEffect(() => {
        const urlParams: { [key: string]: string } = {}
        const paramsString = location.search
        const searchParams = new URLSearchParams(paramsString)
        const queryFilter = searchParams.get('filter')

        if (queryFilter) {
            const filters = queryFilter.split('-')

            filters.forEach((filter) => {
                const [attributeCode, attributeId] = filter.split(':')
                const attributeName = getAttributeName(
                    attributeId,
                    attributeCode
                )
                if (attributeName) urlParams[attributeCode] = attributeName
            })

            if (Object.keys(urlParams).length !== 0)
                setSelectedOptions({ ...selectedOptions, ...urlParams })
        }
    }, [])

    return {
        options,
        selectedOptions,
        setSelectedOptions
    }
}
